import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { DraggableDivider } from 'components/draggable-divider';
import { leftPaneOpenAtom } from './atoms';
import { EvalWorkspace } from './eval-workspace';
import { PageControlsPanel } from './page-controls-panel';
import styles from './left-pane.module.scss';

/**
 * Initial top/bottom split — the Evals tree gets ~45% of the pane height, the
 * Page Controls panel the rest. The divider clamps further drags to 15–85%.
 */
const DEFAULT_TOP_PERCENT = 45;

/**
 * The left column of the eval3-new page: the Evals tree (top) stacked over the existing
 * `PageControlsPanel` (bottom), separated by a draggable divider that resizes the split. The
 * two regions scroll independently. Collapsible — like the right Eval Settings panel, the
 * outer element animates its width (380 ↔ 0) driven by {@link leftPaneOpenAtom} (toggled by the
 * `LeftPaneToggle` in the center title bar), with a fixed-width inner so the content doesn't
 * reflow mid-animation. Open by default (snaps in with no initial animation).
 *
 * Owns the split as local state (single owner + single consumer → `useState`, not an atom;
 * localStorage persistence is a deferred polish). The split percentage is applied as the top
 * region's `flex-basis` inline — the documented exception for a dynamic value that can't be
 * predetermined (the static grow/shrink live in SCSS).
 *
 * @return {JSX.Element} The two-region left pane.
 */
export function LeftPane() {
  const [topPercent, setTopPercent] = useState(DEFAULT_TOP_PERCENT);
  const open = useAtomValue(leftPaneOpenAtom);
  // Withhold the width transition until after first paint so the open-by-default pane snaps
  // into place instead of animating in on mount (mirrors the right Eval Settings panel).
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    setAnimated(true);
  }, []);
  // Mark the collapsed pane `inert` so its tree + controls leave the tab order AND the a11y
  // tree — at width 0 they'd otherwise stay keyboard-focusable. Set via a ref (React 17
  // doesn't type `inert`; `inert` subsumes `aria-hidden`), matching the off-screen Output
  // Aggregation subtree in `Eval3NewContent`.
  const paneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (paneRef.current) {
      paneRef.current.inert = !open;
    }
  }, [open]);

  return (
    <div
      ref={paneRef}
      className={classNames(styles.leftPane, {
        [styles.leftPaneClosed]: !open,
        [styles.animated]: animated,
      })}
    >
      {/* Fixed-width inner so the tree / controls don't reflow while the outer animates width. */}
      <div className={styles.inner}>
        <div
          className={styles.topRegion}
          style={{ flexBasis: `${topPercent}%` }}
        >
          <EvalWorkspace />
        </div>
        <DraggableDivider onDrag={setTopPercent} />
        <div className={styles.bottomRegion}>
          <PageControlsPanel />
        </div>
      </div>
    </div>
  );
}
